import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { Receipt } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import { FilterField, SearchDrawer } from "../../Component/SearchDrawer";

import {
  fetchInvoices,
  fetchCustomers,
  fetchGetMonths,
  fetchGetYears,
  GetProjectByCustomerId,
} from "../../services/InvoiceServices";

import { GetAllInvoices } from "../../Interface/Invoice";

interface InvoiceTableProps {
  drawerOpen: boolean;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ drawerOpen }) => {
  const currentDate = new Date();
  const defaultMonth = currentDate.toLocaleString("default", {
    month: "short",
  });
  const defaultYear = currentDate.getFullYear().toString();

  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [invoices, setInvoices] = useState<GetAllInvoices[]>([]);
  const [customers, setCustomers] = useState<
    { customerId: number; customerName: string }[]
  >([]);
  const [projects, setProjects] = useState<
    { projectId: number; projectName: string }[]
  >([]);
  const [months, setMonths] = useState<{ tableName: string }[]>([]);
  const [years, setYears] = useState<{ tableName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpenState, setDrawerOpenState] = useState(false);

  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);

  type FilterType = Partial<{
    customerName: string;
    projectName: string;
    month: string;
    year: string;
  }>;

  const [filters, setFilters] = useState<FilterType>({
    month: defaultMonth,
    year: defaultYear,
  });

  const navigate = useNavigate();

  const customerData = customers.map((c) => c.customerName);
  const projectData = projects.map((p) => p.projectName);
  const monthData = months.map((m) => m.tableName);
  const yearData = years.map((y) => y.tableName);

  const exportFilters: FilterField[] = [
    { key: "month", label: "Month", type: "dropdown", options: monthData },
    { key: "year", label: "Year", type: "dropdown", options: yearData },
    {
      key: "customerName",
      label: "Customer",
      type: "dropdown",
      options: customerData,
    },
    {
      key: "projectName",
      label: "Project",
      type: "dropdown",
      options: projectData,
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [filters]);

  const loadData = async () => {
    try {
      const customerRes = await fetchCustomers();
      setCustomers(Array.isArray(customerRes.data) ? customerRes.data : []);

      const monthRes = await fetchGetMonths();
      setMonths(Array.isArray(monthRes.data) ? monthRes.data : []);

      const yearRes = await fetchGetYears();
      setYears(Array.isArray(yearRes.data) ? yearRes.data : []);
    } catch (error) {
      console.error("Failed to load filter options:", error);
    }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetchInvoices(filters);
      const data = Array.isArray(res.data) ? res.data : [];

      setInvoices(data);

      const total = data.reduce(
        (sum: number, item: { totalInvoiceAmount: number }) =>
          sum + (item.totalInvoiceAmount || 0),
        0
      );
      setTotalAmount(total);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchFilters: FilterType) => {
    setFilters(searchFilters);
    setDrawerOpenState(false);
  };

  const handleReset = () => {
    setFilters({
      customerName: "",
      projectName: "",
      month: defaultMonth,
      year: defaultYear,
    });
    setProjects([]); // clear projects when reset
    setDrawerOpenState(true);
  };

  const handleRowClick = (row: GetAllInvoices) => {
    navigate("/ViewInvoice", { state: { selectedRowData: row } });
  };

  const invoiceColumns: ColDef[] = [
    {
      field: "customerName",
      headerName: "Customer",
      width: 200,
      suppressSizeToFit: true,
    },
    {
      field: "projectName",
      headerName: "Project",
      width: 200,
      suppressSizeToFit: true,
    },
    { field: "invoiceStatus", headerName: "Status", width: 120, maxWidth: 100 },
    {
      field: "month",
      headerName: "Month",
      cellClass: "center",
      width: 120,
      maxWidth: 80,
    },
    {
      field: "year",
      headerName: "Year",
      cellClass: "center",
      width: 120,
      maxWidth: 70,
    },
    {
      field: "totalHours",
      headerName: "Hours",
      cellClass: "right-align",
      valueFormatter: (params) => `${params.value}`,
      width: 100,
      maxWidth: 100,
    },
    {
      field: "totalInvoiceAmount",
      headerName: "Amount",
      cellClass: "right-align",
      valueFormatter: (params) => `$${params.value.toLocaleString()}`,
      width: 120,
      maxWidth: 120,
    },
    {
      field: "employeeCount",
      headerName: "Count",
      cellClass: "right-align",
      width: 80,
      maxWidth: 80,
    },
  ];

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title={`Invoice Dashboard – $${(totalAmount ?? 0).toLocaleString()}`}
        icon={<Receipt />}
        button1Text="Add Invoice"
        button1Action={() => navigate("/AddInvoice")}
        button2Text="Search"
        button2Action={() => setDrawerOpenState(true)}
      />

      <SearchDrawer
        open={drawerOpenState}
        onClose={() => setDrawerOpenState(false)}
        onSearch={handleSearch}
        onReset={handleReset}
        filterFields={exportFilters}
        onCustomerChange={async (selectedCustomerName: string) => {
          // always clear projects first
          setProjects([]);

          const selectedCustomer = customers.find(
            (c) => c.customerName === selectedCustomerName
          );

          if (selectedCustomer) {
            try {
              const projectRes = await GetProjectByCustomerId(
                selectedCustomer.customerId
              );
              const projectList = Array.isArray(projectRes.data.data)
                ? projectRes.data.data
                : [];
              setProjects(projectList);
            } catch (error) {
              console.error("Error loading projects:", error);
              setProjects([]);
            }
          }
        }}
      />

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box style={containerStyle}>
          <Box style={gridStyle}>
            <AgGridTable
              domLayout="autoHeight"
              customStyles="legacy"
              columns={invoiceColumns}
              data={invoices}
              pagination={false}
              rowSelection="single"
              paginationPerPage={0}
              gridOptions={{
                onRowClicked: (event: any) => handleRowClick(event.data),
              }}
              handleSelectedRows={() => {}}
              noDataComponentText="No invoice details available"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InvoiceTable;
